import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { FacetsService } from './service/facets.service';
import { ResetStore, GetFacets, SetFacet, ApplyFacets, ResetFacets } from './facets.actions';
import type { LoadingModel } from 'src/app/_core/state/loading.model';
import type { FacetGroup, FacetOption, FacetsData } from './models/facets.model';
import { iif, patch, updateItem } from '@ngxs/store/operators';
import { FiltersSearchState } from 'src/app/search/state/filters-search/filters-search.state';
import { RecordsSearchState } from 'src/app/search/state/records-search/records-search.state';

export type FacetsStateModel = FacetsData &
  LoadingModel & {
    totalResourcesSelected: number;
    facetGroupsBackup: FacetGroup[];
  };

export const initialState: FacetsStateModel = {
  sliderTitle: '',
  facetNames: [],
  facetGroups: [],
  isLoading: false,
  totalResourcesSelected: 0,
  facetGroupsBackup: [],
};

@State<FacetsStateModel>({
  name: 'facets',
  defaults: initialState,
})
@Injectable()
export class FacetsState {
  private readonly facetsService = inject(FacetsService);
  private readonly store = inject(Store);

  @Selector()
  static isLoading(state: FacetsStateModel) {
    return state.isLoading;
  }

  @Selector()
  static facetGroups(state: FacetsStateModel) {
    return state.facetGroupsBackup;
  }

  @Selector()
  static title(state: FacetsStateModel) {
    return state.sliderTitle;
  }

  @Selector()
  static criteriaFacets(state: FacetsStateModel) {
    const { parentFacets, subFacets } = state.facetGroups.reduce(
      (acc, group) => {
        acc.parentFacets.push(group.parentFacet);
        acc.subFacets.push(...group.subFacets);
        return acc;
      },
      { parentFacets: [] as FacetOption[], subFacets: [] as FacetOption[] }
    );

    const allSelected = state.facetGroups.every((group) => group.allSelected);
    const selectedParents = parentFacets.filter((parent) => parent.selected).map((parent) => parent.name);
    const selectedSubs = subFacets.filter((sub) => sub.selected).map((sub) => sub.name);

    if (allSelected || (!selectedParents.length && !selectedSubs.length)) {
      return {
        facetNames: state.facetNames,
        facetValues: [...parentFacets.map((parent) => parent.name), ...subFacets.map((sub) => sub.name)],
      };
    }

    return {
      facetNames: state.facetNames,
      facetValues: [...selectedParents, ...selectedSubs],
    };
  }

  @Selector()
  static buttonDisplayInfo(state: FacetsStateModel) {
    const selectedNames: string[] = [];
    const allSelected = state.facetGroups.every((group) => group.allSelected);

    state.facetGroups.forEach((group) => {
      if (group.parentFacet.selected) {
        selectedNames.push(group.parentFacet.label);
      }

      group.subFacets.forEach((sub) => {
        if (sub.selected) {
          selectedNames.push(sub.label);
        }
      });
    });

    const total = selectedNames.length;

    if (allSelected || total === 0) {
      return {
        label: 'search.criteria.all',
        count: 0,
      };
    }

    return {
      label: selectedNames[0],
      count: total > 1 ? total - 1 : 0,
    };
  }

  @Action(GetFacets)
  getFacets(ctx: StateContext<FacetsStateModel>) {
    ctx.setState(
      patch<FacetsStateModel>({
        isLoading: true,
      })
    );

    const state = ctx.getState();
    const filters: string = this.store
      .selectSnapshot(FiltersSearchState.filters)
      ?.reduce((acc, group) => {
        const selectedItem = group.filterItems.find((item) => item.selected);
        if (selectedItem) {
          acc.push(`${group.value}:"${selectedItem.value}"`);
        }
        return acc;
      }, [])
      .join(';');
    const query = this.store.selectSnapshot(RecordsSearchState.query);

    return this.facetsService.getFacets(state, filters, query).pipe(
      tap((response: FacetsData) => {
        ctx.setState(
          patch<FacetsStateModel>({
            ...response,
            facetGroupsBackup: structuredClone(response.facetGroups),
            isLoading: false,
            totalResourcesSelected: this._calculateTotal(response.facetGroups),
          })
        );
      })
    );
  }

  @Action(SetFacet)
  setFacet(ctx: StateContext<FacetsStateModel>, { id, origin }: SetFacet) {
    ctx.setState(
      patch({
        facetGroupsBackup: updateItem<FacetGroup>(
          (group) => group.parentFacet.id === id || group.subFacets.some((s) => s.id === id),
          iif(
            (group) => group.parentFacet.id === id,
            (group) => {
              const targetValue = group.someSelected ? false : !group.parentFacet.selected;

              return {
                ...group,
                parentFacet: { ...group.parentFacet, selected: targetValue },
                subFacets: group!.subFacets.map((facet) => ({ ...facet, selected: targetValue })),
                allSelected: targetValue,
                someSelected: false,
              };
            },
            (group) => {
              const subFacets = group.subFacets.map((facet) =>
                facet.id === id ? { ...facet, selected: !facet.selected } : facet
              );
              const targetValue = subFacets.every((facet) => facet.selected);

              return {
                ...group,
                parentFacet: { ...group.parentFacet, selected: targetValue },
                subFacets,
                allSelected: targetValue,
                someSelected: subFacets.some((facet) => facet.selected) && !targetValue,
              };
            }
          )
        ),
      })
    );

    const updatedState = ctx.getState();
    ctx.setState(
      patch({
        facetGroups: origin === 'home' ? structuredClone(updatedState.facetGroupsBackup) : updatedState.facetGroups,
        totalResourcesSelected: this._calculateTotal(updatedState.facetGroupsBackup),
      })
    );
  }

  @Action(ApplyFacets)
  applyFacets(ctx: StateContext<FacetsStateModel>) {
    const state = ctx.getState();
    ctx.setState(
      patch({
        facetGroups: structuredClone(state.facetGroupsBackup),
      })
    );
  }

  @Action(ResetFacets)
  resetFacets(ctx: StateContext<FacetsStateModel>) {
    const state = ctx.getState();
    const resetGroups = state.facetGroups.map((group) => ({
      ...group,
      parentFacet: { ...group.parentFacet, selected: false },
      subFacets: group.subFacets.map((facet) => ({ ...facet, selected: false })),
      allSelected: false,
      someSelected: false,
    }));

    ctx.setState(
      patch({
        facetGroupsBackup: resetGroups,
        totalResourcesSelected: 0,
      })
    );
  }

  @Action(ResetStore)
  resetStore(ctx: StateContext<FacetsStateModel>) {
    const state = ctx.getState();
    const resetGroups = state.facetGroups.map((group) => ({
      ...group,
      parentFacet: { ...group.parentFacet, selected: false },
      subFacets: group.subFacets.map((facet) => ({ ...facet, selected: false })),
      allSelected: false,
      someSelected: false,
    }));

    ctx.setState(
      patch({
        facetGroups: resetGroups,
        facetGroupsBackup: resetGroups,
        totalResourcesSelected: 0,
      })
    );
  }

  private _calculateTotal(groups: FacetGroup[]): number {
    return groups
      ? groups.reduce((acc, group) => {
          if (group.parentFacet.selected) {
            acc += group.parentFacet.count;
          }

          acc += group.subFacets.reduce((sum, facet) => (facet.selected ? sum + facet.count : sum), 0);
          return acc;
        }, 0)
      : 0;
  }
}
